/* Wrapper around isomorphic-fetch */
/* Modify to collect shared functionality that is required on http requests */
/* First usage: if window object is available, attempt to pick protocol from that, default to http */

import fetch from 'isomorphic-fetch';

const DEFAULT_TIMEOUT = 5000;
const RESPONSE_TYPES = {
  TEXT: 'text',
  JSON: 'json',
};

let windowProtocol;

const isValidTimeout = (timeout) => {
  return timeout > 0;
}

const identity = (value) => {
  return value;
}

const tryParseResponseBody = (res) => {
  let contentType = RESPONSE_TYPES.TEXT;
  if (res && res.headers && res.headers.get) {
    const contentTypeStr = res.headers.get('content-type');
    contentType = contentTypeStr && contentTypeStr.toLowerCase().indexOf('json') > -1 ? RESPONSE_TYPES.JSON : contentType;
  }
  return res[contentType] ? res[contentType]() : Promise.resolve('An error occurred in baseFetch tryParseResponseBody');
}

const createErrorIfBadStatusCode = (res) => {
  if (res && res.status >= 400) {
    return tryParseResponseBody(res).then((body) => {
      const err = new Error(`Request to ${res.url} failed with status ${res.status}`);
      err.status = res.status;
      err.statusText = res.statusText;
      err.response = body;
      return err;
    });
  }
  return res;
}

const throwIfError = (res) => {
  if (res && res.constructor === Error) {
    throw res;
  }
  return res;
}

export const baseFetch = (baseUrl, options = {}) => {
  if (!options.caller) {
    throw new Error('Option "caller" MUST be supplied to fetch!');
  }

  const ptr = {};

  const promise = new Promise((resolve, reject) => {
    ptr.resolve = resolve;
    ptr.reject = reject;
  });

  const handleResponse = options.dontThrowOnBadStatus ? identity : createErrorIfBadStatusCode;

  options.headers = options.headers || {};
  options.headers['x-caller-app'] = options.caller;

  const optionsToUse = {
    ...options,
    timeout: (isValidTimeout(options.timeout) && +options.timeout) || DEFAULT_TIMEOUT,
  };


  if (!baseUrl) {
    return Promise.reject(new Error('Cannot fetch empty url'));
  }

  if (typeof baseUrl !== 'string') {
    return Promise.reject(new Error('Unable to fetch non-string url'));
  }

  // Should this not use the timeout from optionsToUse instead?
  if (options.timeout && typeof window !== 'undefined') {
    ptr.timeout = setTimeout(() => ptr.reject(new Error('Request timed out')), options.timeout);
  }

  if (baseUrl.substring(0, 4).toLowerCase() === 'http') {
    fetch(baseUrl, optionsToUse).then((v) => {
      ptr.timeout && clearTimeout(ptr.timeout);
      return v;
    }).then(handleResponse).then(throwIfError)
      .then(ptr.resolve)
      .catch(ptr.reject);
    return promise;
  }

  /* eslint-disable no-undef */
  windowProtocol = windowProtocol || (typeof window !== 'undefined' && window.location && window.location.protocol);
  const protocol = windowProtocol || 'http:';
  /* eslint-enable no-undef */

  let url;
  if (baseUrl.indexOf('//') === 0) {
    url = `${protocol}${baseUrl}`;
  } else if (baseUrl.indexOf('/') === 0) {
    url = baseUrl;
  } else {
    url = `${protocol}//${baseUrl}`;
  }

  fetch(url, optionsToUse).then((v) => {
    ptr.timeout && clearTimeout(ptr.timeout);
    return v;
  }).then(handleResponse).then(throwIfError)
    .then(ptr.resolve)
    .catch(ptr.reject);

  return promise;
};
